import { formatDate } from '@/lib/orgFormat';
import { dayInZone } from '@/features/learning/lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { BookOpen, CheckCircle, Award, Plus, Loader2, ExternalLink, Settings2, Target } from 'lucide-react';
import toast from 'react-hot-toast';
import * as api from '@/services/learningService';
import { getApiErrorMessage } from '@/lib/apiError';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useHasPermission } from '@/rbac/useRbac';
import { useCompleteAssignment, useMyActions, useMyAssignments, useMyJourney, useMyReadiness, useSelfAttendance } from '@/features/learning/api/hooks';
import { NextTrainingCard } from '@/features/learning/components/NextTrainingCard';
import { TrainingJourney } from '@/features/learning/components/TrainingJourney';
import { ActionRequired } from '@/features/learning/components/ActionRequired';
import { SessionDetailSheet } from '@/features/learning/components/SessionDetailSheet';
import { PracticeDialog } from '@/features/learning/components/PracticeDialog';
import { ReadinessBadge } from '@/features/learning/components/ReadinessBadge';

// Learning & Development — employee self-service ONLY. Programs + Sessions
// (Phase 1) add a journey-first top: Next training, my journey, action
// required. When the viewer is on no program the page collapses to the
// course-centric layout (My learning / My certifications / Course catalog).
// Administration lives under Administration → Learning (LearningAdminPage);
// people managers get Team Learning. Deep link: training/sessions/:sessionId.
export const TrainingPage: React.FC = () => {
  const { t } = useTranslation(['training']);
  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams<{ sessionId?: string }>();
  const qc = useQueryClient();
  const { isHrManager, isTenantOwner } = useRoleAccess();
  // Shortcut visibility only — the target pages + backend enforce the permissions.
  const canManageLearning = useHasPermission('learning:manage') || isHrManager || isTenantOwner;
  const canAssignLearning = useHasPermission('learning:assign');

  const coursesQ = useQuery({ queryKey: ['learning', 'courses'], queryFn: api.listCourses, staleTime: 60_000 });
  const mineQ = useMyAssignments();
  const certsQ = useQuery({ queryKey: ['learning', 'me', 'certifications'], queryFn: () => api.listCertifications(), staleTime: 60_000 });
  const journeyQ = useMyJourney();
  const actionsQ = useMyActions();
  // Phase 5: my go-live readiness per program (server-computed; the card hides when there are no entries).
  const readinessQ = useMyReadiness();
  const myReadiness = readinessQ.data ?? [];
  const completeM = useCompleteAssignment();
  const selfAtt = useSelfAttendance();
  const courses = coursesQ.data ?? [];
  const mine = mineQ.data ?? [];
  const certs = certsQ.data ?? [];
  const journeyPrograms = journeyQ.data?.programs ?? [];
  const hasJourney = journeyPrograms.length > 0;

  const [busy, setBusy] = useState(false);
  const [certDraft, setCertDraft] = useState<any>(null);
  const [completeFor, setCompleteFor] = useState<api.Assignment | null>(null);
  const [evidence, setEvidence] = useState('');
  const [openSession, setOpenSession] = useState<{ id: string; initial?: api.Session | null } | null>(null);
  // Phase 4: a 'practice' action on a course-only assignment opens the practice dialog.
  const [practiceFor, setPracticeFor] = useState<{ assignmentId: string; title: string } | null>(null);
  useEffect(() => { if (routeSessionId) setOpenSession({ id: routeSessionId }); }, [routeSessionId]);
  const closeSession = () => {
    setOpenSession(null);
    if (routeSessionId) navigate('../..', { relative: 'path' });
  };

  const open = mine.filter((a) => a.status === 'assigned' || a.status === 'in_progress');
  const done = mine.filter((a) => a.status === 'completed' || a.status === 'verified');
  const statusChip: Record<string, string> = {
    assigned: 'bg-muted text-muted-foreground', in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
    completed: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300', verified: 'bg-success-soft text-success-strong', cancelled: 'bg-muted text-muted-foreground',
  };
  const courseById = useMemo(() => Object.fromEntries(courses.map((c) => [c.id, c])), [courses]);
  const sessionTitleById = useMemo(() => {
    const m: Record<string, string> = {};
    journeyPrograms.forEach((p) => p.sessions.forEach((s) => { m[s.id] = s.title; }));
    return m;
  }, [journeyPrograms]);
  const reloadCerts = () => qc.invalidateQueries({ queryKey: ['learning', 'me', 'certifications'] });

  const complete = () => {
    if (!completeFor) return;
    completeM.mutate({ id: completeFor.id, body: { evidence_url: evidence || undefined } }, {
      onSuccess: () => { toast.success(t('training:completed_ok', 'Marked complete')); setCompleteFor(null); setEvidence(''); },
    });
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('training:title', 'Learning & Development')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('training:subtitle', 'Your assigned courses, completions and certifications.')}</p>
        </div>
        {canManageLearning ? (
          <Button size="sm" variant="outline" asChild>
            <Link to="../admin/learning"><Settings2 className="mr-1 h-4 w-4" />{t('training:manage_link', 'Learning administration')}</Link>
          </Button>
        ) : canAssignLearning ? (
          <Button size="sm" variant="outline" asChild>
            <Link to="../team-learning"><Settings2 className="mr-1 h-4 w-4" />{t('training:team_link', 'Team Learning')}</Link>
          </Button>
        ) : null}
      </div>

      {/* Journey-first top (Programs + Sessions). Hidden entirely when the viewer is on no program. */}
      {hasJourney && (
        <>
          {journeyQ.data?.next && (
            <NextTrainingCard session={journeyQ.data.next} onView={(s) => setOpenSession({ id: s.id, initial: s })} checkingIn={selfAtt.isPending}
              onCheckIn={(s) => selfAtt.mutate({ sessionId: s.id, action: 'checkin' }, { onSuccess: () => toast.success(t('training:att_checked_in_ok', 'Checked in')) })} />
          )}
          {myReadiness.length > 0 && (
            <Card data-testid="my-readiness"><CardContent className="pt-4 space-y-2.5">
              <CardTitle className="text-base inline-flex items-center gap-2"><Target className="h-4 w-4 text-orange-500" aria-hidden />{t('training:your_readiness', 'Your readiness')}</CardTitle>
              <div className="grid gap-2 sm:grid-cols-2">
                {myReadiness.map((r) => (
                  <div key={r.program_id} className="rounded-md border border-border p-3 space-y-1.5 text-sm" data-testid={`my-readiness-${r.program_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-foreground">{r.program_name}</span>
                      <ReadinessBadge state={r.state} />
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      {t('training:readiness_attended_of', '{{a}}/{{r}} attended', { a: r.attended_sessions, r: r.required_sessions })}
                      {r.upcoming_sessions > 0 ? ` · ${t('training:readiness_upcoming_n', '{{n}} upcoming', { n: r.upcoming_sessions })}` : ''}
                      {r.practices_total > 0 ? ` · ${t('training:readiness_validated_of', '{{v}}/{{n}} validated', { v: r.practices_validated, n: r.practices_total })}` : ''}
                    </div>
                    {(r.blockers || []).length > 0 && (
                      <ul className="list-disc pl-4 text-xs text-foreground space-y-0.5" data-testid="my-readiness-blockers">{r.blockers.map((b, i) => <li key={`${i}-${b}`}>{b}</li>)}</ul>
                    )}
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
          <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-start">
            <TrainingJourney programs={journeyPrograms} onOpen={(s) => setOpenSession({ id: s.id, initial: s })} />
            <ActionRequired actions={actionsQ.data ?? []} onOpen={(id) => setOpenSession({ id })}
              onPractice={(a) => (a.session_id ? setOpenSession({ id: a.session_id }) : a.assignment_id ? setPracticeFor({ assignmentId: a.assignment_id, title: a.title }) : undefined)} />
          </div>
        </>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[{ icon: BookOpen, label: t('training:stat_open', 'Open assignments'), v: open.length },
          { icon: CheckCircle, label: t('training:stat_done', 'Completed'), v: done.length },
          { icon: Award, label: t('training:stat_certs', 'Certifications'), v: certs.length }].map((s, i) => (
          <Card key={i}><CardContent className="pt-4 flex items-center gap-3">
            <s.icon className="w-6 h-6 text-orange-500" />
            <div><div className="text-xl font-bold tabular-nums">{s.v}</div><div className="text-[11px] text-muted-foreground">{s.label}</div></div>
          </CardContent></Card>
        ))}
      </div>

      {/* My assignments */}
      <Card><CardContent className="pt-4 space-y-2.5">
        <CardTitle className="text-base">{t('training:my_learning', 'My learning')}</CardTitle>
        {mine.length === 0 && <p className="text-sm text-muted-foreground">{t('training:none', 'Nothing assigned to you yet.')}</p>}
        {mine.map((a) => {
          const isSession = !!a.session_id;
          const title = isSession ? (sessionTitleById[a.session_id as string] || a.course_title || t('training:session_id', 'Session')) : a.course_title;
          const courseUrl = a.course_id ? courseById[a.course_id]?.url : undefined;
          return (
            <div key={a.id} className="rounded-md border border-border p-3 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-sm text-foreground flex flex-wrap items-center gap-2">
                  {isSession ? <button type="button" className="hover:underline text-left" onClick={() => setOpenSession({ id: a.session_id as string })}>{title}</button> : title}
                  <Badge className={`border-0 text-[10px] ${statusChip[a.status] || ''}`}>{a.status.replace('_', ' ')}</Badge>
                  {isSession && <Badge variant="outline" className="text-[10px]">{t('training:session_tag', 'Session')}</Badge>}
                  {a.source === 'requirement' && <Badge variant="outline" className="text-[10px]">{t('training:required', 'Required')}</Badge>}
                  {a.source === 'renewal' && <Badge variant="outline" className="text-[10px]">{t('training:renewal', 'Renewal')}</Badge>}
                  {a.source === 'external' && <Badge variant="outline" className="text-[10px]">{t('training:external_src', 'External')}</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {a.due_date ? `${t('training:due', 'Due')} ${formatDate(dayInZone(a.due_date, a.session_timezone))}` : ''}
                  {courseUrl ? <a className="ml-2 inline-flex items-center gap-1 text-orange-600 hover:underline" href={courseUrl} target="_blank" rel="noreferrer">{t('training:open_course', 'Open course')} <ExternalLink className="h-3 w-3" /></a> : null}
                </div>
              </div>
              {(a.status === 'assigned' || a.status === 'in_progress') && (
                <Button size="sm" onClick={() => { setCompleteFor(a); setEvidence(''); }}>{t('training:mark_complete', 'Mark complete')}</Button>
              )}
            </div>
          );
        })}
        {completeFor && (
          <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-3 space-y-2">
            <div className="text-sm font-medium">{t('training:complete_title', 'Complete')} “{completeFor.session_id ? (sessionTitleById[completeFor.session_id] || completeFor.course_title) : completeFor.course_title}”</div>
            <div className="space-y-1"><Label className="text-xs">{t('training:evidence', 'Evidence link (optional)')}</Label>
              <Input className="h-8" value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="https://…" /></div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setCompleteFor(null)}>{t('training:cancel', 'Cancel')}</Button>
              <Button size="sm" onClick={complete} disabled={completeM.isPending}>{completeM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:confirm_complete', 'Complete')}</Button>
            </div>
          </div>
        )}
      </CardContent></Card>

      {/* Certifications */}
      <Card><CardContent className="pt-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('training:certifications', 'My certifications')}</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setCertDraft({ name: '' })}><Plus className="mr-1 h-4 w-4" />{t('training:add_cert', 'Add')}</Button>
        </div>
        {certs.length === 0 && <p className="text-sm text-muted-foreground">{t('training:no_certs', 'No certifications recorded.')}</p>}
        {certs.map((c) => (
          <div key={c.id} className="text-sm flex flex-wrap justify-between gap-2 rounded-md border border-border p-2.5">
            <span className="font-medium">{c.name}</span>
            <span className="text-xs text-muted-foreground">{c.issuer || ''}{c.expires_on ? ` · ${t('training:expires', 'expires')} ${formatDate(c.expires_on)}` : ''}</span>
          </div>
        ))}
        {certDraft && (
          <div className="rounded-md border border-border p-3 grid sm:grid-cols-2 gap-2 items-end">
            <div className="space-y-1"><Label className="text-xs">{t('training:cert_name', 'Name')}</Label><Input className="h-8" value={certDraft.name} onChange={(e) => setCertDraft({ ...certDraft, name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('training:issuer', 'Issuer')}</Label><Input className="h-8" value={certDraft.issuer || ''} onChange={(e) => setCertDraft({ ...certDraft, issuer: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('training:expires_on', 'Expires (optional)')}</Label><Input className="h-8" type="date" value={certDraft.expires_on || ''} onChange={(e) => setCertDraft({ ...certDraft, expires_on: e.target.value })} /></div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setCertDraft(null)}>{t('training:cancel', 'Cancel')}</Button>
              <Button size="sm" disabled={busy || !certDraft.name} onClick={async () => {
                setBusy(true);
                try { await api.addCertification({ name: certDraft.name, issuer: certDraft.issuer, expires_on: certDraft.expires_on ? new Date(certDraft.expires_on).toISOString() : undefined }); setCertDraft(null); reloadCerts(); }
                catch (e: any) { toast.error(getApiErrorMessage(e, t('training:err', 'Something went wrong'))); }
                finally { setBusy(false); }
              }}>{t('training:save', 'Save')}</Button>
            </div>
          </div>
        )}
      </CardContent></Card>

      {/* Catalog (browse only — management moved to Administration → Learning) */}
      <Card><CardContent className="pt-4 space-y-2.5">
        <CardTitle className="text-base">{t('training:catalog', 'Course catalog')}</CardTitle>
        {courses.length === 0 && <p className="text-sm text-muted-foreground">{t('training:no_courses', 'No courses yet.')}</p>}
        {courses.map((c) => (
          <div key={c.id} className="rounded-md border border-border p-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="min-w-0">
              <span className="font-medium">{c.title}</span>
              <span className="text-xs text-muted-foreground"> · {c.provider_kind}{c.provider_name ? ` · ${c.provider_name}` : ''}{c.duration_hours ? ` · ${c.duration_hours}h` : ''}{c.validity_months ? ` · ${t('training:renews_every', 'renews every')} ${c.validity_months}mo` : ''}</span>
              {(c.competencies || []).length > 0 && <div className="flex flex-wrap gap-1 mt-0.5">{(c.competencies || []).map((k) => <span key={k} className="text-[10px] rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{k}</span>)}</div>}
              {c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}
            </div>
            {c.url && <a className="inline-flex items-center gap-1 text-xs text-orange-600 hover:underline" href={c.url} target="_blank" rel="noreferrer">{t('training:open_course', 'Open course')} <ExternalLink className="h-3 w-3" /></a>}
          </div>
        ))}
      </CardContent></Card>

      <SessionDetailSheet sessionId={openSession?.id ?? null} initial={openSession?.initial ?? null} onClose={closeSession} />
      {practiceFor && <PracticeDialog assignmentId={practiceFor.assignmentId} title={practiceFor.title} onClose={() => setPracticeFor(null)} />}
    </div>
  );
};

export default TrainingPage;
